import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { CreditCardIcon, EditIcon, LoopIcon, ShareIcon } from '@change/ds-icons';
import type { Icon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core-react/i18n';

type TimelineStep = Readonly<{
	key: string;
	icon: React.FC<Icon>;
	title: React.ReactNode;
	body: React.ReactNode;
}>;

const TIMELINE_STEPS: readonly TimelineStep[] = [
	{
		key: 'step_1',
		icon: EditIcon,
		title: <Translate value="corgi.ambassadors.deal.timeline.step_1.title" />,
		body: <Translate value="corgi.ambassadors.deal.timeline.step_1.body" />,
	},
	{
		key: 'step_2',
		icon: ShareIcon,
		title: <Translate value="corgi.ambassadors.deal.timeline.step_2.title" />,
		body: <Translate value="corgi.ambassadors.deal.timeline.step_2.body" />,
	},
	{
		key: 'step_3',
		icon: CreditCardIcon,
		title: <Translate value="corgi.ambassadors.deal.timeline.step_3.title" />,
		body: <Translate value="corgi.ambassadors.deal.timeline.step_3.body" />,
	},
	{
		key: 'step_4',
		icon: LoopIcon,
		title: <Translate value="corgi.ambassadors.deal.timeline.step_4.title" />,
		body: <Translate value="corgi.ambassadors.deal.timeline.step_4.body" />,
	},
];

const TimelineStepItem: React.FC<{ step: TimelineStep }> = ({ step }) => {
	const StepIcon = step.icon;

	return (
		<li className="relative z-10 flex flex-col items-center text-center">
			<div className="border-stroke-brand-strong bg-background-base mb-4 flex size-14 items-center justify-center rounded-full border-2">
				<StepIcon size={26} className="text-fill-brand-strong" />
			</div>
			<Text as="h3" variant="heading" size="x-small" className="text-typography-strong mb-2">
				{step.title}
			</Text>
			<Text as="p" variant="body" size="small" className="text-typography-weak max-w-[280px]">
				{step.body}
			</Text>
		</li>
	);
};

export const TheDeal: React.FC = () => {
	return (
		<section id="deal" className="py-16 lg:py-24">
			<div className="container">
				<Text
					as="span"
					variant="body"
					size="small"
					fontWeight="bold"
					className="text-stroke-brand-strong tracking-wide uppercase"
				>
					<Translate value="corgi.ambassadors.deal.label" />
				</Text>
				<Text as="h2" variant="heading" size="large" className="text-typography-strong mt-2">
					<Translate value="corgi.ambassadors.deal.heading" />
				</Text>
				<Text as="p" variant="body" size="large" className="text-typography-weak mt-2 mb-8 max-w-xl">
					<Translate value="corgi.ambassadors.deal.sub" />
				</Text>
				<div className="relative mt-4">
					<div
						aria-hidden
						className="border-stroke-weaker absolute top-7 right-[12.5%] left-[12.5%] hidden border-t lg:block"
					/>
					<ol className="grid grid-cols-1 gap-10 lg:grid-cols-4 lg:gap-6">
						{TIMELINE_STEPS.map((step) => (
							<TimelineStepItem key={step.key} step={step} />
						))}
					</ol>
				</div>
			</div>
		</section>
	);
};
